import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router";
import { SearchBar } from "@/components/common/SearchBar";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import { TooltipButton } from "@/components/common/TooltipButton";
import {
  codePointLength,
  MIN_QUERY_CODE_POINTS,
  useAppSearch,
} from "./useAppSearch";
import {
  flattenResults,
  navigateSearchRow,
  optionDomId,
  rowKey,
} from "./searchRows";
import { SearchResultsPanel } from "./SearchResultsPanel";

interface AppSearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  alwaysExpanded?: boolean;
  size?: "sm" | "lg";
  dropdownWidth?: string;
  trailingAdornment?: React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
}

export const AppSearchBox: React.FC<AppSearchBoxProps> = ({
  value,
  onChange,
  placeholder = "Search",
  alwaysExpanded = false,
  size = "sm",
  dropdownWidth,
  trailingAdornment,
  inputRef,
}) => {
  const [expanded, setExpanded] = useState(alwaysExpanded);
  const [dismissed, setDismissed] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [anchorReady, setAnchorReady] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const { results, isSearching, error } = useAppSearch(value);
  const rows = useMemo(() => flattenResults(results), [results]);
  const modelIdsInResults = useMemo(
    () => new Set(results?.models.map((model) => model.id) ?? []),
    [results],
  );

  const trimmed = value.trim();
  const dropdownOpen =
    expanded && anchorReady && !dismissed && trimmed.length > 0;
  const belowGate = codePointLength(trimmed) < MIN_QUERY_CODE_POINTS;

  useEffect(() => {
    if (value && !expanded) setExpanded(true);
  }, [value, expanded]);

  useEffect(() => {
    setAnchorReady(expanded && anchorRef.current !== null);
  }, [expanded]);

  useEffect(() => {
    setDismissed(false);
    setActiveKey(null);
  }, [value]);

  const activeIndex = rows.findIndex((item) => rowKey(item) === activeKey);

  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(optionDomId(rows[activeIndex]))
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, rows]);

  const moveActive = (delta: 1 | -1): void => {
    if (rows.length === 0) return;
    const next =
      activeIndex < 0
        ? delta === 1
          ? 0
          : rows.length - 1
        : (activeIndex + delta + rows.length) % rows.length;
    setActiveKey(rowKey(rows[next]));
  };

  const collapse = (): void => {
    onChange("");
    if (!alwaysExpanded) setExpanded(false);
    setDismissed(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!dropdownOpen) setDismissed(false);
      moveActive(e.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (dropdownOpen && activeIndex >= 0)
        navigateSearchRow(rows[activeIndex], navigate);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      if (dropdownOpen) {
        setDismissed(true);
      } else {
        collapse();
      }
    }
  };

  if (!expanded) {
    return (
      <TooltipButton
        title="Search"
        variant="default"
        size="xs"
        onClick={() => setExpanded(true)}
      >
        <SearchIcon />
      </TooltipButton>
    );
  }

  const highlightedKey = hoveredKey ?? activeKey;
  const highlightedRow = rows.find((item) => rowKey(item) === highlightedKey);
  const washedModelId =
    highlightedRow?.kind === "table" &&
    highlightedRow.table.parent_model.name !== null &&
    modelIdsInResults.has(highlightedRow.table.parent_model.id)
      ? highlightedRow.table.parent_model.id
      : null;

  return (
    <div className={alwaysExpanded ? "w-full" : "max-w-80 w-full"}>
      <SearchBar
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        searchBarRef={anchorRef}
        onClose={alwaysExpanded ? undefined : collapse}
        onKeyDown={handleKeyDown}
        size={size}
        trailingAdornment={trailingAdornment}
        inputProps={{
          role: "combobox",
          "aria-expanded": dropdownOpen,
          "aria-controls": "app-search-listbox",
          "aria-activedescendant":
            dropdownOpen && activeIndex >= 0
              ? optionDomId(rows[activeIndex])
              : undefined,
          "aria-autocomplete": "list",
          autoFocus: !alwaysExpanded,
          ref: inputRef,
        }}
      />
      <DropdownMenu
        anchorRef={anchorRef}
        isOpen={dropdownOpen}
        onClose={() => setDismissed(true)}
        style={
          dropdownWidth
            ? { width: dropdownWidth }
            : alwaysExpanded
              ? undefined
              : { width: "min(26rem, calc(100vw - 2rem))" }
        }
        shouldIgnoreOutsideEvent={(e) =>
          e.target instanceof Element &&
          e.target.closest(".MuiTooltip-popper") !== null
        }
      >
        {belowGate ? (
          <div className="px-1 pb-1">
            <div className="px-2 py-3 text-small text-fg-muted text-center">
              Keep typing — search starts at {MIN_QUERY_CODE_POINTS} characters
            </div>
          </div>
        ) : (
          <SearchResultsPanel
            results={results}
            isSearching={isSearching}
            error={error}
            rows={rows}
            activeKey={activeKey}
            onHoverKey={setHoveredKey}
            washedModelId={washedModelId}
            modelIdsInResults={modelIdsInResults}
          />
        )}
      </DropdownMenu>
    </div>
  );
};
